import { CheckCircle } from "@mui/icons-material";
import React from "react";
import { toast } from "react-toastify";

const RenderSeatMap = ({
  cabin,
  selection,
  idx,
  selectedSeats,
  setSelectedSeats,
  activeSeatTab,
  passengers,
}) => {
  const getCabinDisplayName = (cabinType) => {
    const cabinNames = {
      C: "Business Class",
      Y: "Economy Class",
      F: "First Class",
      W: "Premium Economy",
    };
    return cabinNames[cabinType] || "Standard Class";
  };

  const getSeatTypeIcon = (seat) => {
    const characteristics = seat.seatCharacteristics;
    if (characteristics?.includes("EXTRA_LEGROOM")) return "🦵";
    if (characteristics?.includes("WINDOW")) return "🪟";
    if (characteristics?.includes("AISLE")) return "🚶";
    return "";
  };

  const getSeatButtonClass = (seat, isSelected) => {
    const baseClasses =
      "relative w-10 h-10 text-xs font-semibold rounded-xl transition-all duration-300 transform hover:scale-105 active:scale-95 shadow-sm";

    if (isSelected) {
      return `${baseClasses} bg-gradient-to-br from-blue-500 to-blue-600 text-white ring-2 ring-blue-300 ring-offset-2 shadow-lg`;
    }

    switch (seat.seatAvailability) {
      case "VAC": {
        const price = parseFloat(seat.seatCharacteristics) || 0;
        if (price > 1) {
          return `${baseClasses} bg-gradient-to-br from-emerald-100 to-emerald-200 border-2 border-emerald-300 text-emerald-800 hover:from-emerald-200 hover:to-emerald-300`;
        }
        //  else if (price > 20) {
        //   return `${baseClasses} bg-gradient-to-br from-amber-100 to-amber-200 border-2 border-amber-300 text-amber-800 hover:from-amber-200 hover:to-amber-300`;
        // }
        // else if (price < 0.01) {
        return `${baseClasses} bg-gradient-to-br from-green-500 to-green-600 text-white ring-2 ring-green-300 ring-offset-2 shadow-lg`;
        // }
        // return `${baseClasses} bg-gradient-to-br from-emerald-100 to-emerald-200 border-2 border-emerald-300 text-emerald-800 hover:from-emerald-200 hover:to-emerald-300`;
      }

      case "RES": // Reserved
        return `${baseClasses} bg-gradient-to-br from-slate-300 to-slate-400 border-2 border-slate-400 text-slate-600 cursor-not-allowed`;

      case "INA": // Inactive
        return `${baseClasses} bg-gradient-to-br from-red-100 to-red-200 border-2 border-red-300 text-red-500 cursor-not-allowed opacity-60`;

      default:
        return `${baseClasses} bg-gradient-to-br from-gray-100 to-gray-200 border-2 border-gray-300 text-gray-600`;
    }
  };

  const handleSeatSelection = (seat, row, selection, segmentIdx) => {
    if (seat.seatAvailability === "INA" || seat.seatAvailability === "RES") {
      // toast.error("Please Choose free seat");
      return;
    }

    const seatId = `${row.rowNumber}${seat.seatNumber}`;

    setSelectedSeats((prev) => {
      const newSelectedSeats = { ...prev };

      if (!newSelectedSeats[activeSeatTab]) {
        newSelectedSeats[activeSeatTab] = {};
      }

      newSelectedSeats[activeSeatTab][segmentIdx] = {
        ...seat,
        seatId,
        rowNumber: row.rowNumber,
        seatNumber: seat.seatNumber,
        rph: selection.rph,
        DepartureDate: selection.departureDateTime,
        FlightNumber: selection.flightNumber,
        passengerIndex: activeSeatTab,
        currencyCode: seat.currencyCode || "USD",
        TravelerRefNumberRPHList: passengers?.[activeSeatTab]?.refNumber,
      };

      return newSelectedSeats;
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center p-4 bg-gradient-to-r to-blue-50 rounded-xl border from-slate-50 border-slate-200">
        <h5 className="flex gap-2 items-center text-xl font-bold text-slate-800">
          <span className="text-2xl">
            {cabin.cabinType === "C" ? "💼" : "✈️"}
          </span>
          {getCabinDisplayName(cabin.cabinType)}
        </h5>
        <div className="px-3 py-1 text-sm bg-white rounded-full text-slate-600">
          {cabin.rows?.length || 0} rows available
        </div>
      </div>

      <div className="p-3 bg-white rounded-xl border shadow-sm border-slate-200">
        <div className="space-y-3">
          {cabin.rows?.map((row) => {
            const leftSeats = row.seats?.slice(0, 3) || [];
            const rightSeats = row.seats?.slice(3, 6) || [];

            return (
              <div
                key={row.rowNumber}
                className="flex gap-4 justify-center items-center"
              >
                {/* Row Number */}
                <div className="w-4 text-center">
                  <span className="px-2 py-1 text-sm font-bold rounded-md text-slate-700 bg-slate-100">
                    {row.rowNumber}
                  </span>
                </div>

                {/* Left Side Seats (A, B, C) */}
                <div className="flex gap-2">
                  {leftSeats.map((seat) => {
                    const seatId = `${row.rowNumber}${seat.seatNumber}`;
                    const isSelected =
                      selectedSeats?.[activeSeatTab]?.[idx]?.seatId === seatId;

                    return (
                      <div key={seatId} className="relative group">
                        <button
                          className={getSeatButtonClass(seat, isSelected)}
                          disabled={
                            seat.seatAvailability === "INA" ||
                            seat.seatAvailability === "RES"
                          }
                          onClick={() =>
                            handleSeatSelection(seat, row, selection, idx)
                          }
                          title={seat.seatCharacteristics || ""}
                        >
                          <span className="relative z-10">
                            {seat.seatNumber}
                          </span>
                          {isSelected && (
                            <div className="flex absolute inset-0 justify-center items-center">
                              <CheckCircle className="w-4 h-4 text-main" />
                            </div>
                          )}
                          {getSeatTypeIcon(seat) && (
                            <span className="absolute -top-1 -right-1 text-xs">
                              {getSeatTypeIcon(seat)}
                            </span>
                          )}
                        </button>
                        {seat.seatCharacteristics && (
                          <div className="absolute bottom-full left-1/2 z-10 px-2 py-1 mb-2 text-xs font-medium text-white whitespace-nowrap bg-gray-900 rounded opacity-0 transition-opacity transform -translate-x-1/2 pointer-events-none group-hover:opacity-100">
                            {seat.seatCharacteristics}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* Aisle Space */}
                <div className="flex justify-center w-8">
                  <div className="w-px h-6 bg-slate-300"></div>
                </div>

                {/* Right Side Seats (D, E, F) */}
                <div className="flex gap-2">
                  {rightSeats.map((seat) => {
                    const seatId = `${row.rowNumber}${seat.seatNumber}`;
                    const isSelected =
                      selectedSeats?.[activeSeatTab]?.[idx]?.seatId === seatId;

                    return (
                      <div key={seatId} className="relative group">
                        <button
                          className={getSeatButtonClass(seat, isSelected)}
                          disabled={
                            seat.seatAvailability === "INA" ||
                            seat.seatAvailability === "RES"
                          }
                          onClick={() =>
                            handleSeatSelection(seat, row, selection, idx)
                          }
                          title={seat.seatCharacteristics || ""}
                        >
                          <span className="relative z-10">
                            {seat.seatNumber}
                          </span>
                          {isSelected && (
                            <div className="flex absolute inset-0 justify-center items-center">
                              <CheckCircle className="w-4 h-4 text-main" />
                            </div>
                          )}
                          {getSeatTypeIcon(seat) && (
                            <span className="absolute -top-1 -right-1 text-xs">
                              {getSeatTypeIcon(seat)}
                            </span>
                          )}
                        </button>
                        {seat.seatCharacteristics && (
                          <div className="absolute bottom-full left-1/2 z-10 px-2 py-1 mb-2 text-xs font-medium text-white whitespace-nowrap bg-gray-900 rounded opacity-0 transition-opacity transform -translate-x-1/2 pointer-events-none group-hover:opacity-100">
                            {seat.seatCharacteristics}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* Row Number (Right) */}
                <div className="w-8 text-center">
                  <span className="px-2 py-1 text-sm font-bold rounded-md text-slate-700 bg-slate-100">
                    {row.rowNumber}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default RenderSeatMap;
